import React from 'react';
import { Container, Accordion } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    q: 'What is Family Ring?',
    a: "Family Ring is the modern way to capture your family's stories and legacy. It's a private mobile app combining guided video interviews with your parents and grandparents, weekly video check-ins across the whole family, shared celebrations, and Selfie\u2122 — a private AI memory trained on a family member's recorded stories. One subscription covers up to 10 family members. Family Ring is a software application, not a jewelry product. It is published by Howell Co."
  },
  {
    q: 'Is there a free trial?',
    a: "Yes — the first six months are free with full access to every feature. After the trial it's $4.99/month for your whole family (up to 10 family members) on one subscription. You can cancel anytime."
  },
  {
    q: 'How much does Family Ring cost?',
    a: "Free for the first six months. After that, $4.99/month for the base plan, which covers up to 10 family members on one subscription. Larger plans are available: $6.99/month for up to 15 members, $9.99/month for up to 25. Annual billing is available at a discount. One subscription, whole family — no per-seat cost."
  },
  {
    q: 'How is Family Ring different from StoryWorth?',
    a: "StoryWorth was built in 2013 around weekly emails and a year-end hardcover book. Family Ring is the same job — capturing your family's stories and legacy — built for how families actually communicate today. Video, not text. AI-enhanced prompts. Your whole family on one subscription. Six months free, then $4.99/month. See our full StoryWorth comparison for details."
  },
  {
    q: 'How is Family Ring different from group chats?',
    a: "Group chats are built for real-time messaging. Every meaningful moment eventually scrolls away. Family Ring is built to keep things: weekly video check-ins, recorded interviews with older family members, and celebrations you can go back to years later."
  },
  {
    q: 'How many family members can I add?',
    a: "Up to 10 on the base plan, 15 on the mid tier, and 25 on the top tier. Family Ring is built around multi-generational use — parents, siblings, spouses, children, grandparents, cousins. One subscription covers your whole family; there is no per-seat cost."
  },
  {
    q: 'Can I download my videos?',
    a: "Yes. You can download your videos anytime — during your trial, while subscribed, or after canceling. Your family's stories are yours; we don't lock them up."
  },
  {
    q: 'What is Selfie?',
    a: "Selfie\u2122 is a private AI memory that Family Ring builds for a family member after they've recorded a few videos. The family can ask Selfie questions and get answers based on what that person actually said in their stories. Today Selfie is text-based; voice and video are possible in the future depending on what families ask for. Selfie is private to the family ring it belongs to and is never trained on anything outside that family."
  },
  {
    q: 'Is Family Ring private?',
    a: "Yes. Family Ring is invite-only. There is no public feed, no algorithmic recommendation, and no discoverability. Only people you invite into your family ring can see your family's stories."
  },
  {
    q: "Does Family Ring work if my parents aren't tech-savvy?",
    a: "Yes. The app was designed specifically with older relatives in mind. Recording a video answer is a single tap. Weekly Callouts arrive as one notification with the question and a record button — no navigating menus, no typing. Families with grandparents who were nervous at first are usually comfortable within a week."
  },
  {
    q: 'Is Family Ring available on Android?',
    a: "Yes. Family Ring is available on both iOS through the Apple App Store and Android through the Google Play Store. One subscription covers your whole family across both platforms."
  },
  {
    q: 'What happens if I cancel my subscription?',
    a: "You keep access to the videos you've already recorded, and you can download them anytime — before, during, or after canceling. New recording features pause when you're not subscribed, but your library doesn't disappear. Resubscribe anytime to resume active use."
  },
  {
    q: 'How do I invite family members?',
    a: "From within the app, you generate an invite link or send an invitation by email. The person you invite installs the app and joins your family ring. They won't see anything from outside your invited family circle — it's entirely private to the group you build."
  },
  {
    q: "What's the difference between Weekly Callouts and Interviews?",
    a: "Weekly Callouts are short, family-wide: one question sent to everyone in your ring, and each person records a quick video answer. Interviews are longer, one-on-one, and use AI-enhanced follow-up prompts to draw out fuller stories — designed for interviewing older relatives about their lives."
  },
  {
    q: 'Is Family Ring the jewelry brand?',
    a: "No — that's a different product and a different company. Family Ring (the app) is a mobile application for private family video stories, published by Howell Co. You can download it from the Apple App Store and Google Play."
  }
];

function FAQPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>FAQ — Family Ring App | Modern Family Story & Legacy App</title>
        <meta
          name="description"
          content="Answers to common questions about Family Ring — the modern family story and legacy app. Pricing, free trial, Selfie, privacy, and how it compares to StoryWorth."
        />
        <link rel="canonical" href="https://familyring.io/faq" />
        <meta property="og:title" content="FAQ — Family Ring" />
        <meta
          property="og:description"
          content="Common questions about Family Ring — the modern family story and legacy app. Pricing, free trial, Selfie, privacy."
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section className="py-5">
        <Container style={{ maxWidth: 820 }}>
          <div className="text-center mb-5">
            <h1 className="section-title">Frequently asked questions</h1>
            <p className="text-muted">
              Everything families want to know about Family Ring. Still have a
              question? Email us at{' '}
              <a href="mailto:support@familyring.io">support@familyring.io</a>.
            </p>
          </div>
          <Accordion>
            {faqs.map((item, i) => (
              <Accordion.Item eventKey={String(i)} key={item.q}>
                <Accordion.Header>{item.q}</Accordion.Header>
                <Accordion.Body>{item.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>
    </>
  );
}

export default FAQPage;
